import React, { useCallback, useContext, useEffect, useState } from 'react';
import {Eye, FileWarning, Search, Filter, RefreshCw, Download, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import { SecretsApi, DataLeak, PaginatedDataLeakList } from 'api-client';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { TablePagination } from '../../components/ui/TablePagination';
import { Link } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

/**
 * Props for the DataLeaksList component
 */
interface DataLeaksListProps {
  /** Data leaks to display (optional, will fetch from API if not provided) */
  leaks?: DataLeak[];
  /** Current page number for pagination (optional) */
  currentPage?: number;
  /** Function to handle page changes (optional) */
  onPageChange?: (page: number) => void;
  /** Total number of leaks for pagination (optional) */
  totalLeaks?: number;
  /** Number of items per page (optional) */
  itemsPerPage?: number;
  /** Pagination information object (optional) */
  paginationInfo?: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  /** Whether to show the header section (optional) */
  showHeader?: boolean;
  /** Whether to show the filters section (optional) */
  showFilters?: boolean;
}

/**
 * DataLeaksList component that can either use provided data or fetch from API
 */
const DataLeaksList: React.FC<DataLeaksListProps> = ({
  leaks: providedLeaks,
  currentPage: providedCurrentPage,
  onPageChange: providedOnPageChange,
  totalLeaks,
  itemsPerPage = 10,
  paginationInfo: providedPaginationInfo,
  showHeader = true,
  showFilters = true,
}) => {
  const navigate = useNavigate();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(providedCurrentPage || 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaks, setLeaks] = useState<DataLeak[]>(providedLeaks || []);
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>(providedPaginationInfo || { count: totalLeaks || 0, next: null, previous: null });

  // Handle page changes, either using provided handler or internal state
  const handlePageChange = (page: number) => {
    if (providedOnPageChange) {
      providedOnPageChange(page);
    } else {
      setCurrentPage(page);
    }
  };

  const fetchLeaks = useCallback(async () => {
    // Skip API call if data is provided externally
    if (providedLeaks) return;
    
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new SecretsApi(getApiConfiguration(token));
      const response = await api.dataLeaksList({
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
      });
      setLeaks(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching data leaks:', err);
      setError('Failed to load data leaks');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery, providedLeaks]);

  const fetchAllLeaks = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new SecretsApi(getApiConfiguration(token));
    const response = await api.dataLeaksList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      limit
    });
    return response.results;
  };

  useEffect(() => {
    // Update local state if props change
    if (providedLeaks) {
      setLeaks(providedLeaks);
    }
    if (providedCurrentPage) {
      setCurrentPage(providedCurrentPage);
    }
    if (providedPaginationInfo) {
      setPaginationInfo(providedPaginationInfo);
    } else if (totalLeaks !== undefined) {
      setPaginationInfo(prev => ({ ...prev, count: totalLeaks }));
    }
  }, [providedLeaks, providedCurrentPage, providedPaginationInfo, totalLeaks]);

  useEffect(() => {
    // Only fetch data if not provided externally
    if (!providedLeaks) {
      fetchLeaks();
    }
  }, [fetchLeaks, providedLeaks]);

  return (
    <div className="space-y-6">
      {/* Header - Only show if showHeader is true */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileWarning className="h-8 w-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Data Leaks</h1>
              <p className="text-sm text-gray-400">Comprehensive list of detected data leaks</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <ExportActionBtn
              fetchAllData={() => fetchAllLeaks(paginationInfo.count)}
              filename="data_leaks"
              disabled={leaks?.length === 0}
            />
            {/* <Button
              onClick={fetchLeaks}
              disabled={isLoading || !!providedLeaks}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button> */}
          </div>
        </div>
      )}

      {/* Filters - Only show if showFilters is true */}
      {showFilters && (
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
            <input
              type="text"
              placeholder="Search data leaks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
      )}

      {/* Leaks Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Snippet</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Category</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Source</th> */}
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Severity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Created</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-red-400">
                  {error}
                </td>
              </tr>
            ) : leaks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                  No data leaks found
                </td>
              </tr>
            ) : (
              leaks.map((leak) => (
                <tr 
                  key={leak.id} 
                  className="group hover:bg-gray-800/30 cursor-pointer" 
                  onClick={() => navigate(`/data-protection/data-leaks/${leak.id}`)}
                >
                  <td className="px-6 py-4">
                    <span className="text-sm text-white">{leak.leakType?.toUpperCase()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-white truncate max-w-xs block overflow-hidden">{leak.snippet}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">{leak.category || '-'}</span>
                  </td>
                  {/* <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">
                      {leak.sourceLink ? (
                        <a 
                          href={leak.sourceLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-400"
                        >
                          View Source
                        </a>
                      ) : '-'}
                    </span>
                  </td> */}
                  <td className="px-6 py-4">
                    <Badge variant={leak.severity as any}>{leak.severity?.toLocaleUpperCase()}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={leak.status as any}>{leak.status?.toLocaleUpperCase()}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">
                      {new Date(leak.createdAt).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/data-protection/data-leaks/${leak.id}`}
                      className="bg-[#1A1F2E] text-white"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                  </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!isLoading && !error && paginationInfo.count > 0 && (
          <TablePagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalItems={paginationInfo.count}
            itemsPerPage={itemsPerPage}
            paginationInfo={paginationInfo}
            itemName="data leaks"
          />
        )}
      </div>
    </div>
  );
};

export default DataLeaksList;